import React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import IconButton from "@material-ui/core/IconButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Paper from "@material-ui/core/Paper";

import TextImage from "../TextImage";

import { hasCategoryType, hasShades, hasSingleSideColor } from "../../helpers";

const useStyles = makeStyles(theme => ({
  container: {
    overflowX: "auto",
    overflow: "auto",
    [theme.breakpoints.down("sm")]: {
      overflowX: "hidden",
      overflow: "hidden"
    }
  },
  table: {
    // minWidth: 650
  },
  cellHide: {
    display: "none"
  },
  head: {
    [theme.breakpoints.down("sm")]: {
      display: "none"
    }
  },
  headCell: {
    fontWeight: "700"
  },
  stackedCommon: {
    [theme.breakpoints.down("sm")]: {
      display: "inline-block",
      fontSize: "16px",
      height: props => (props.tall ? theme.spacing(20) : theme.spacing(10)),
      whiteSpace: "normal",
      width: "calc(50% - 80px)",
      overflowY: "auto",
      "&:last-child": {
        borderBottom: "none"
      },
      "&:nth-last-child(2)": {
        borderBottom: "none"
      }
    }
  },
  cellStackedSmall: {
    [theme.breakpoints.down("sm")]: {
      width: "50%"
    }
  },
  responsiveStackedSmall: {
    [theme.breakpoints.down("sm")]: {
      width: "50%"
    }
  },
  row: {
    [theme.breakpoints.down("sm")]: {
      borderBottom: "solid 3px #fbb800",
      marginBottom: theme.spacing(2)
    }
  },
  textField: {
    margin: 0,
    width: "3rem",
    "& input": {
      padding: "0.25rem",
      textAlign: "center"
    }
  }
}));

function createData(name, calories, fat, carbs, protein) {
  return { name, calories, fat, carbs, protein };
}

const rows = [
  createData("Frozen yoghurt", 159, 6.0, 24, 4.0),
  createData("Ice cream sandwich", 237, 9.0, 37, 4.3),
  createData("Eclair", 262, 16.0, 24, 6.0),
  createData("Cupcake", 305, 3.7, 67, 4.3),
  createData("Gingerbread", 356, 16.0, 49, 3.9)
];

// function CellHeader() {
//   return (
//
//   )
// }

function handleCountBlur(event, id, onCountChange) {
  const value = Math.max(event.target.value, 1);

  onCountChange(value, id);
}

function CellBody({ children, columnHeader, onClick, tall, ...otherProps }) {
  const classes = useStyles({ tall });

  return [
    <TableCell
      key={1}
      className={classNames({
        [classes.headCell]: true,
        [classes.cellHide]: true,
        [classes.stackedCommon]: true,
        [classes.cellStackedSmall]: true
      })}
    >
      {columnHeader}
    </TableCell>,
    <TableCell
      key={2}
      onClick={onClick}
      className={classNames({
        [classes.stackedCommon]: true,
        [classes.responsiveStackedSmall]: true
      })}
      {...otherProps}
    >
      {children}
    </TableCell>
  ];
}

export default function ProductTable({
  items,
  onCountChange,
  onRemove,
  productId
}) {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <TableContainer component={Paper}>
      <div style={{ position: "relative" }} className={classes.container}>
        <Table className={classes.table} aria-label="simple table">
          <TableHead className={classes.head}>
            <TableRow>
              <TableCell className={classes.headCell}>
                {t("categories.type")}
              </TableCell>
              <TableCell className={classes.headCell} align="right">
                {t("categories.count")}
              </TableCell>
              <TableCell className={classes.headCell} align="right">
                {t(
                  `categories.${
                    hasCategoryType(items.map(({ material }) => material))
                      ? "category"
                      : "material"
                  }`
                )}
              </TableCell>
              <TableCell className={classes.headCell} align="right">
                {t("categories.system")}
              </TableCell>
              {hasSingleSideColor(productId) ? (
                <TableCell className={classes.headCell} align="right">
                  {t("categories.color")}
                </TableCell>
              ) : (
                <>
                  <TableCell className={classes.headCell} align="right">
                    {t("categories.color interior")}
                  </TableCell>
                  <TableCell className={classes.headCell} align="right">
                    {t("categories.color exterior")}
                  </TableCell>
                </>
              )}
              <TableCell className={classes.headCell} align="right">
                {t("categories.dimensions")}
              </TableCell>
              {hasShades(productId) ? (
                <TableCell className={classes.headCell} align="right">
                  {t("categories.shades")}
                </TableCell>
              ) : null}
              <TableCell className={classes.headCell} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(
              ({
                id,
                color,
                colorInterior,
                colorExterior,
                count,
                material,
                selectedShades,
                system,
                width,
                height,
                type
              }) => (
                <TableRow className={classes.row} key={id}>
                  <CellBody
                    tall
                    columnHeader={t("categories.type")}
                    component="th"
                    scope="row"
                  >
                    <TextImage
                      image={type.image}
                      text={t(
                        `names.${type.isSubtype ? "subtypes" : "types"}.${
                          type.name
                        }`
                      )}
                      center
                    />
                  </CellBody>
                  <CellBody columnHeader={t("categories.count")} align="right">
                    <TextField
                      className={classes.textField}
                      id="count"
                      type="number"
                      // label={t("order.form.name")}
                      InputLabelProps={{
                        shrink: true
                      }}
                      variant="outlined"
                      value={count}
                      onChange={event => onCountChange(event.target.value, id)}
                      onBlur={event =>
                        handleCountBlur(event, id, onCountChange)
                      }
                      margin="normal"
                      // helperText={errors.name}
                      // fullWidth
                      size="small"
                    />
                  </CellBody>
                  <CellBody
                    columnHeader={t(
                      `categories.${
                        hasCategoryType(items.map(({ material }) => material))
                          ? "category"
                          : "material"
                      }`
                    )}
                    align="right"
                  >
                    {material && t(`names.materials.${material.name}`)}
                  </CellBody>
                  <CellBody columnHeader={t("categories.system")} align="right">
                    {t(`names.systems.${system.name}`)}
                  </CellBody>
                  {hasSingleSideColor(productId) ? (
                    <CellBody
                      columnHeader={t("categories.color")}
                      align="right"
                    >
                      {t(`names.colors.${color.name}`)}
                    </CellBody>
                  ) : (
                    <>
                      <CellBody
                        columnHeader={t("categories.color interior")}
                        align="right"
                      >
                        {t(`names.colors.${colorInterior.name}`)}
                      </CellBody>
                      <CellBody
                        columnHeader={t("categories.color exterior")}
                        align="right"
                      >
                        {t(`names.colors.${colorExterior.name}`)}
                      </CellBody>
                    </>
                  )}
                  <CellBody
                    columnHeader={t("categories.dimensions")}
                    align="right"
                  >
                    {width} x {height}
                  </CellBody>
                  {hasShades(productId) ? (
                    <CellBody
                      columnHeader={t("categories.shades")}
                      align="right"
                    >
                      {selectedShades && selectedShades.value
                        ? t("root.yes")
                        : t("root.no")}
                    </CellBody>
                  ) : null}
                  <CellBody columnHeader="" align="right">
                    <IconButton
                      aria-label="delete"
                      color="secondary"
                      onClick={() => onRemove(id)}
                      title={t("order.remove")}
                    >
                      <DeleteForeverIcon />
                    </IconButton>
                  </CellBody>
                  {/* <TableCell align="right">{row.protein}</TableCell> */}
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </div>
    </TableContainer>
  );
}
